import type { ReactNode } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import {
  Dialog,
  DialogContent,
  type DialogProps,
  DialogTrigger,
} from "@b2bportal/core-ui/overridable";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import type { UsePricePredictionResponse } from "../PricePredictionCard/hooks/usePricePrediction";
import {
  type IntervalKey,
  PricePredictionInterval,
} from "../PricePredictionInterval/PricePredictionInterval";
import { usePricePredictionDialog } from "./hooks/usePricePredictionDialog";
import styles from "./styles.module.scss";

interface PricePredictionDialogProps
  extends DialogProps,
    UsePricePredictionResponse {
  className: string;

  watchTripButton: ReactNode;
}

export const PricePredictionDialog = ({
  className,
  open,
  onClose,
  onOpen,
  children,
  watchTripButton,
  priceDropDialogProps,
  ...props
}: PricePredictionDialogProps): JSX.Element => {
  const [block, cn] = useModuleBEM(styles, "PricePredictionDialog");
  const {
    t,
    brand: { urls },
  } = useI18nContext();
  const {
    context: {
      isRoundTrip,
      isPDPEligible,
      priceDropAmountPrice,
      currentLowestPrice,
      forecastMaxPrice,
      forecastMinPrice,
      shouldWait,
      intervals = {},
    },
  } = usePricePredictionDialog();

  return (
    <Dialog open={open} onClose={onClose} onOpen={onOpen}>
      <DialogTrigger>
        <button className={className}>{children}</button>
      </DialogTrigger>

      <DialogContent
        className={block}
        {...props}
        title={t("pricePrediction.header")}
        bottomFixedContent={
          <div className={cn("footer-buttons")}>
            <a
              className={cn("terms-and-conditions-link")}
              href={urls.termsAndConditions}
              target="_blank"
            >
              {t("termsAndConditions")}
            </a>

            {watchTripButton}
            <button className={cn("cta-button")} onClick={onClose}>
              {t("pricePrediction.bookNowIcon")}
            </button>
          </div>
        }
      >
        <p className={cn("subtitle")}>{t("howToUse")}</p>

        <p className={cn("description")}>
          <Trans
            i18nKey={
              isPDPEligible
                ? "flightShopPrediction.description"
                : "flightShopPrediction.descriptionWithoutPriceDrop"
            }
            components={[
              <span
                className={cn("price-drop-button")}
                onClick={priceDropDialogProps.onOpen}
              />,
            ]}
            values={{
              travelCreditAmount: priceDropAmountPrice,
            }}
          />
        </p>

        <p className={cn("disclaimer")}>
          <Trans i18nKey="flightShopPrediction.disclaimer" />
        </p>

        <p className={cn("subtitle")}>
          {t("flightShopPrediction.lowestPriceTitle")}
        </p>

        <p className={cn("description")}>
          <span className={cn("lowest-price")}>{currentLowestPrice}</span>

          {t(
            `pricePrediction.lowestPrice${
              isRoundTrip ? "RoundTrip" : "OneWay"
            }Suffix`
          )}
        </p>

        {shouldWait ? (
          <>
            <p className={cn("subtitle")}>
              {t("pricePrediction.forecastedLowest")}
            </p>

            <p className={cn("description")}>
              <span className={cn("lowest-price")}>{forecastMinPrice}</span>

              {t(
                isRoundTrip
                  ? "pricePrediction.lowestPriceRoundTripSuffix"
                  : "pricePrediction.lowestPriceOneWaySuffix"
              )}
            </p>
          </>
        ) : (
          <>
            <p className={cn("subtitle")}>
              {t("pricePrediction.forecastedHighest")}
            </p>

            <p className={cn("description")}>
              <span className={cn("highest-price")}>{forecastMaxPrice}</span>

              {t(
                isRoundTrip
                  ? "pricePrediction.lowestPriceRoundTripSuffix"
                  : "pricePrediction.lowestPriceOneWaySuffix"
              )}
            </p>
          </>
        )}

        {/* Show Intervals */}
        {intervals &&
          Object.keys(intervals).map((key) => (
            <PricePredictionInterval
              key={key}
              intervalKey={key as IntervalKey}
              interval={intervals[key]}
            />
          ))}
      </DialogContent>
    </Dialog>
  );
};
